import { z } from 'zod'
import * as m from '~/paraglide/messages'
import { locationFormSchema, tagsInputSchema, tagsResponseSchema } from './shared-form-schemas'

export const listingLocationSchema = locationFormSchema

const timeRangeSchema = z.object({
  open: z.string().regex(/^\d{2}:\d{2}:\d{2}$/, m.timeMustBeInFormatHhMmSs()),
  close: z.string().regex(/^\d{2}:\d{2}:\d{2}$/, m.timeMustBeInFormatHhMmSs()),
})

// DayOfWeek enum: 0=Sunday, 1=Monday, 2=Tuesday, 3=Wednesday, 4=Thursday, 5=Friday, 6=Saturday
export const openingHoursSchema = z.record(
  z.enum(['0', '1', '2', '3', '4', '5', '6']),
  z.array(timeRangeSchema),
)

export const openingHourSchema = z.object({
  day: z.enum(['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']),
  open: z.string().regex(/^\d{2}:\d{2}:\d{2}$/, m.timeMustBeInFormatHhMmSs()),
  close: z.string().regex(/^\d{2}:\d{2}:\d{2}$/, m.timeMustBeInFormatHhMmSs()),
})

export const listingBackendSchema = z.object({
  id: z.string(),
  name: z.string().min(1, m.listingNameRequired()),
  description: z.string().min(10, m.descriptionMustBeAtLeast10Characters()),

  location: z.object({
    name: z.string().nullable(),
    street: z.string(),
    city: z.string(),
    country: z.string(),
    coordinates: z.object({
      lat: z.number(),
      lng: z.number(),
    }),
  }),

  rating: z.number().min(0).max(5).optional(),
  reviewCount: z.number().default(0),
  interestedCount: z.number().default(0),

  phone: z.string().nullable().optional(),
  website: z.string().nullable().optional(),
  email: z.string().nullable().optional(),

  facebook: z.string().nullable().optional(),
  instagram: z.string().nullable().optional(),
  twitter: z.string().nullable().optional(),
  nicknames: z.array(z.string()).default([]),
  postalCode: z.string().nullable().optional(),
  priceLevel: z.number().nullable().optional(),

  coverImageUrl: z.string().optional(),
  images: z
    .array(
      z.object({
        id: z.string(),
        url: z.string(),
        sortOrder: z.number().default(0),
      }),
    )
    .default([]),

  hours: z.array(openingHourSchema),

  status: z.enum(['Draft', 'Published', 'Importing']).optional(),
  isDraft: z.boolean().default(false),

  tags: tagsResponseSchema,

  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
})

export const listingSchema = listingBackendSchema.extend({
  hours: z.array(openingHourSchema).default([]),
})

const priceLevelValueSchema = z.enum(['', '1', '2', '3', '4'])

/** The price level as the form holds it: '' for not stated, '1' to '4' for € to €€€€. */
export type PriceLevelValue = z.infer<typeof priceLevelValueSchema>

export function toPriceLevelValue(level: number | null | undefined): PriceLevelValue {
  const value = level == null ? '' : String(level)
  return priceLevelValueSchema.safeParse(value).data ?? ''
}

export const createListingSchema = z.object({
  name: z.string().min(1, m.listingNameRequired()),
  description: z.string().min(10, m.descriptionMustBeAtLeast10Characters()),
  location: listingLocationSchema,
  postalCode: z.string().trim().max(12).optional().or(z.literal('')),
  priceLevel: priceLevelValueSchema.optional(),
  phone: z.string().optional().or(z.literal('')),
  website: z.string().pipe(z.url()).optional().or(z.literal('')),
  email: z.email().optional().or(z.literal('')),
  facebook: z.string().optional().or(z.literal('')),
  instagram: z.string().optional().or(z.literal('')),
  twitter: z.string().optional().or(z.literal('')),
  nicknames: z.string().optional().or(z.literal('')),
  hours: z.array(openingHourSchema).default([]),
  isDraft: z.boolean().default(false),
  tags: tagsInputSchema,
})

export const updateListingSchema = createListingSchema.partial().extend({
  id: z.string(),
})

export type Listing = z.infer<typeof listingSchema>
export type ListingLocation = z.infer<typeof listingLocationSchema>
export type OpeningHours = z.infer<typeof openingHoursSchema>
export type OpeningHour = z.infer<typeof openingHourSchema>
export type CreateListingInput = z.infer<typeof createListingSchema>
export type UpdateListingInput = z.infer<typeof updateListingSchema>
